import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';


export interface DurationFilter {
  minutes: number;
  selected: boolean;
}

@Component({
  selector: 'app-duration-filter-multi',
  standalone: true,
  imports: [],
  templateUrl: './duration-filter-multi.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duration-filter-multi.component.css']
})
export class DurationFilterMultiComponent implements OnInit, OnChanges {
  @Input() availableIntervals: number[] = [];
  @Input() loading = false;
  @Input() selectedFilters: boolean[] = []; // New input
  @Input() allSelected = true; // New input

  @Output() filterChanged = new EventEmitter<{
    selectedIntervals: number[],
    selectedFilters: boolean[],
    allSelected: boolean
  }>();

  durationFilters: DurationFilter[] = [];

  // Internal loading state - starts as true to prevent flash of "no options" message
  internalLoading = true;
  
  ngOnInit(): void {
    this.initFilters();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    console.log('duration filter changes:', changes)

    // When parent loading state changes, update internal state
    if (changes['loading']) {
      if (this.loading) {
        // Parent started loading - show loading indicator
        this.internalLoading = true;
      } else {
        // Parent finished loading - we can now show content (or "no options" if empty)
        this.internalLoading = false;
      }
    }

    if (changes['availableIntervals']) {
      // If we have selectedFilters with matching length, use them
      if (this.selectedFilters.length === this.availableIntervals.length) {
        this.durationFilters = this.availableIntervals.map((minutes, i) => ({
          minutes,
          selected: this.selectedFilters[i]
        }));
        this.allSelected = this.allSelected;
      } else {
        // Otherwise initialize all as selected
        this.initFilters();
      }

      // If we have data, we're definitely done loading
      // If data is empty but parent is not loading, we're also done (genuinely no options)
      // If data is empty and parent is loading, keep showing loading
      if (this.availableIntervals.length > 0) {
        this.internalLoading = false;
      } else if (!this.loading) {
        // Parent finished loading with empty result - show "no options" message
        this.internalLoading = false;
      }
      // If data is empty and parent is loading, keep internalLoading as true
    }
  }
  
  private initFilters(): void {
    if (this.availableIntervals && this.availableIntervals.length > 0) {
      // Initialize with all options selected
      this.durationFilters = this.availableIntervals.map(minutes => ({
        minutes,
        selected: true
      }));
      this.allSelected = true;
    } else {
      this.durationFilters = [];
      this.allSelected = true;
    }
  }
  
  toggleAll(): void {
    // Toggle the all selected state
    this.allSelected = !this.allSelected;
    
    // Update all individual filters to match
    this.durationFilters.forEach(filter => filter.selected = this.allSelected);
    
    // Emit the change
    this.emitChange();
  }
  
  toggleFilter(filter: DurationFilter): void {
    // Toggle the selected state of the clicked filter
    filter.selected = !filter.selected;
    
    // Check if all filters are selected
    this.updateAllSelectedState();
    
    // Emit the change
    this.emitChange();
  }
  
  private updateAllSelectedState(): void {
    this.allSelected = this.durationFilters.every(filter => filter.selected);
  }
  
  private emitChange(): void {
    // Create updated selectedFilters array
    const selectedFilters = this.durationFilters.map(filter => filter.selected);
    
    if (this.allSelected) {
      // If all are selected, emit all available intervals
      this.filterChanged.emit({
        selectedIntervals: this.availableIntervals,
        selectedFilters,
        allSelected: true
      });
    } else {
      // Otherwise emit only the selected intervals
      const selectedIntervals = this.durationFilters
        .filter(filter => filter.selected)
        .map(filter => filter.minutes);
      this.filterChanged.emit({
        selectedIntervals,
        selectedFilters,
        allSelected: false
      });
    }
  }

  // Add trackBy function for better performance
  trackByMinutes(index: number, item: DurationFilter): number {
    return item.minutes;
  }

  // Add these helper methods to safely check conditions
  hasSelectedFilters(): boolean {
    return this.durationFilters.some(filter => filter.selected);
  }

  shouldShowResetButton(): boolean {
    return !this.allSelected && this.hasSelectedFilters();
  }
}